import {
  AccessTime,
  Add,
  ArrowRight,
  Chat,
  Close,
  HeadsetMic,
  InfoOutlined,
  KeyboardArrowRight,
  Mail,
} from "@mui/icons-material";
import React, { useState } from "react";
import AudioPlayerForRouting from "../reusables/AudioPlayerForRouting";
import { useRouteConfigModals } from "../contexts/RouteConfigModalsContext";
import ConfigureCallActionModal from "../modals/ConfigureCallActionModal";
import TimePicker from "react-time-picker";
import { Info, Settings2 } from "lucide-react";
import { Divider } from "@mui/material";
import ConfigureCallActionForBusinessModal from "../modals/ConfigureCallActionForBusinessHours";
import ConfigureNotificationForBusinessHours from "../modals/ConfigureNotificationForBusinessHours";
import ConfigureNotificationForAfterHours from "../modals/ConfigureNotificationForAfterHours";
import ConfigureCallActionForAfterModal from "../modals/ConfigureCallActionForAfterHours";
import ConfigureNotificationModal from "../modals/ConfigureNotificationMainModal";
import TooltipComponent from "../reusables/TooltipComponent";
import IvrMenuOption from "./IvrMenuOption";
import { getNextAvailableIvrDigit, normalizeIvrOptionsMap, sortIvrOptionEntries } from "./routePayloadNormalize";

const IvrMenu = ({ setOpenConfigureSlotModal, route, setRoute, hasValidationError = false, enableBusinessHours = false }) => {
  // Legacy routes may still have options as an array (key "0" for Press 1); normalize once.
  React.useEffect(() => {
    const opts = route?.ivrMenu?.options;
    if (!Array.isArray(opts)) return;
    setRoute((prev) => ({
      ...prev,
      ivrMenu: { ...prev.ivrMenu, options: normalizeIvrOptionsMap(opts) },
    }));
  }, [route?.ivrMenu?.options, setRoute]);

  const [openConfigureCallActionModal, setOpenConfigureCallActionModal] =
    useState(false);

  const [
    openConfigureCallActionForBusinessHoursModal,
    setOpenConfigureCallActionForBusinessHoursModal,
  ] = useState(false);

  const [
    openConfigureCallActionForAfterHoursModal,
    setOpenConfigureCallActionForAfterHoursModal,
  ] = useState(false);

  const [
    openConfigureNotificationForAfterHoursModal,
    setOpenConfigureNotificationForAfterHoursModal,
  ] = useState(false);

  const [
    openConfigureNotificationForBusinessHoursModal,
    setOpenConfigureNotificationForBusinessHoursModal,
  ] = useState(false);

  const [
    openConfigureNotificationMainModal,
    setOpenConfigureNotificationMainModal,
  ] = useState(false);

  const routeModals = useRouteConfigModals();
  const [localRecordings] = useState(
    () => JSON.parse(localStorage.getItem("organization"))?.recordings?.L || []
  );
  const [localChannels] = useState(
    () => JSON.parse(localStorage.getItem("organization"))?.channels?.L || []
  );
  const recordings = routeModals?.recordings ?? localRecordings;
  const channels = routeModals?.channels ?? localChannels;

  return (
    <div className={`border-2 border-gray-200 rounded-xl p-6 space-y-4 shadow-sm ${hasValidationError ? "bg-red-50/80" : "bg-white"}`}>
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center space-x-2 text-lg font-semibold text-gray-900">
            <Settings2 className="h-6 w-6 text-indigo-500" />
            <span>Call Menu (IVR)</span>
          </div>
          <p className="text-sm text-gray-500">
            Let callers choose with keypad
          </p>
        </div>
        <button
          className="text-sm text-blue-600 font-medium hover:underline"
          onClick={() => setOpenConfigureSlotModal(true)}
        >
          ↻ Change
        </button>
      </div>

      {/* Menu Recording */}
      <div className="space-y-1">
        <div className="flex items-center text-sm font-medium text-gray-700">
          Menu Recording
          <TooltipComponent message={"The recording that plays when callers reach this menu"}/>
        </div>
        <select
          defaultValue={route?.ivrMenu?.menu_recording || "disabled"}
          onChange={(e) => {
            setRoute({
              ...route,
              ivrMenu: { ...route.ivrMenu, menu_recording: e.target.value },
            });
          }}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option disabled value="disabled">
            Select a recording
          </option>
          {recordings &&
            recordings.map((item) => (
              <option key={item.M.rec_id.S} value={item.M.rec_id.S}>{item.M.title.S}</option>
            ))}
        </select>

        {route?.ivrMenu?.menu_recording && (
          <AudioPlayerForRouting
            src={
              recordings.find(
                (e) => e.M.rec_id.S == route?.ivrMenu?.menu_recording
              ).M.url.S
            }
          />
        )}
      </div>

      <div className="space-y-1">
        <div className="flex items-center text-sm font-medium text-gray-700">
          Hangup Recording
          <TooltipComponent message={"A recording that plays before disconnecting the call if no response is received from the caller within 10 seconds"}/>
        </div>
        <select
          defaultValue={route?.ivrMenu?.hangup_recording || "disabled"}
          onChange={(e) => {
            setRoute({
              ...route,
              ivrMenu: { ...route.ivrMenu, hangup_recording: e.target.value },
            });
          }}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option disabled value="disabled">
            Select a recording
          </option>
          {recordings &&
            recordings.map((item) => (
              <option key={item.M.rec_id.S} value={item.M.rec_id.S}>{item.M.title.S}</option>
            ))}
        </select>

        {route?.ivrMenu?.hangup_recording && (
          <AudioPlayerForRouting
            src={
              recordings.find(
                (e) => e.M.rec_id.S == route?.ivrMenu?.hangup_recording
              ).M.url.S
            }
          />
        )}
      </div>

      {/* Menu Options */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm font-medium text-gray-700">
          <div className="flex items-center">
            Menu Options
            <TooltipComponent message={"Configure what happens when callers press different keys"}/>
          </div>
          <button
            className="text-blue-600 hover:underline text-sm font-medium flex items-center"
            onClick={() => {
              const opts = route?.ivrMenu?.options ?? {};
              const nextDigit = getNextAvailableIvrDigit(opts);
              if (!nextDigit) return;
              setRoute({
                ...route,
                ivrMenu: {
                  ...route.ivrMenu,
                  options: {
                    ...opts,
                    [nextDigit]: {
                      digit: nextDigit,
                      id: `press_${nextDigit}`,
                      name: `Press ${nextDigit}`,
                      primaryAction: null,
                      notification: null,
                    },
                  },
                },
              });
            }}
          >
            + Add Option
          </button>
        </div>

        {/* Menu option cards: options keyed by DTMF digit ("1", "2", ...) */}
        {route?.ivrMenu?.options &&
          sortIvrOptionEntries(Object.entries(route.ivrMenu.options)).map(([optionKey, item], idx) => (
              <IvrMenuOption key={optionKey} route={route} setRoute={setRoute} item={item} optionKey={optionKey} index={idx} enableBusinessHours={enableBusinessHours} />
            ))}
      </div>
    </div>
  );
};

export default IvrMenu;

